import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControl,
  Grid,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import SelectEntity from "components/SelectEntity";
import { isValid } from "date-fns";
import { formatQualification } from "helpers/FormatHelpers";
import useGlobalContext from "hooks/globalData";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  GetQualificationsRequest,
  ListRequestOfBaseFilter,
  ListResponseOfProfession,
  ListResponseOfState,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";
import { standardDateFormat } from "utils/dateUtils";

type SkuQualificationFilterProps = {
  skuId: string;
};

const SkuQualificationFilter = ({ skuId }: SkuQualificationFilterProps) => {
  const { formatMessage: t } = useIntl();
  const {
    stateService,
    qualificationService,
    skuService,
  } = useGlobalContext();

  const organizationId = contextStore.useState((s) => s.organizationId);
  
  const creditSubmissionExpirationDateFromStored = persistanceStore.useState(
    (s) => s.creditSubmissionExpirationDateFrom
  );

  const creditSubmissionExpirationDateToStored = persistanceStore.useState(
    (s) => s.creditSubmissionExpirationDateTo
  );

  const qualificationIdStored = persistanceStore.useState(
    (s) => s.qualificationId
  );

  const stateIdStored = persistanceStore.useState((s) => s.stateId);
  const professionIdStored = persistanceStore.useState((s) => s.professionId);

  const onUpdateStartDate = (date: Date) => {
    if (isValid(date) || date == null)
      persistanceStore.update((s) => {
        s.creditSubmissionExpirationDateFrom = date;
      });
  };

  const onUpdateEndDate = (date: Date) => {
    if (isValid(date) || date == null)
      persistanceStore.update((s) => {
        s.creditSubmissionExpirationDateTo = date;
      });
  };

  const fetchedStates = useQuery<ListResponseOfState>(
    ["fetchedStatesFilter", skuId],
    () =>
      stateService.getStates(
        ListRequestOfBaseFilter.fromJS({
          sortField: "stateName",
        })
      )
  );

  const fetchedQualifications = useQuery(
    ["fetchedQualificationsFilter", skuId, organizationId],
    async () => {
      const result = await qualificationService.getQualifications(
        GetQualificationsRequest.fromJS({
          organizationId,
          sortField: "value",
        })
      );
      return {
        ...result,
        data: result.data?.map((qual) => ({
          ...qual,
          value: formatQualification(qual),
        })),
      };
    }
  );

  const fetchedProfessions = useQuery<ListResponseOfProfession>(
    ["fetchedProfessionsFilter", skuId],
    () =>
      skuService.getProfessions(
        ListRequestOfBaseFilter.fromJS({
          sortField: "name",
        })
      )
  );

  return (
    <>
      <Accordion sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{t({ id: "filtersLabel" })}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={11} sm={12} md={4}>
              <SelectEntity
                value={qualificationIdStored ?? ""}
                fetchedData={fetchedQualifications}
                label={t({ id: "selectQualificationLabel" })}
                onChange={(id) =>
                  persistanceStore.update((s) => {
                    s.qualificationId = id!;
                  })
                }
                textFieldProps={{
                  variant: "outlined",
                }}
                labelField={"value"}
              />
            </Grid>
            <Grid item xs={11} sm={12} md={4}>
              <SelectEntity
                value={professionIdStored ?? ""}
                fetchedData={fetchedProfessions}
                label={t({ id: "selectProfessionLabel" })}
                onChange={(id) =>
                  persistanceStore.update((s) => {
                    s.professionId = id!;
                  })
                }
                textFieldProps={{
                  variant: "outlined",
                }}
                labelField={"name"}
              />
            </Grid>
            <Grid item xs={11} sm={12} md={4}>
              <SelectEntity
                value={stateIdStored ?? ""}
                fetchedData={fetchedStates}
                label={t({ id: "selectStateLabel" })}
                onChange={(id) =>
                  persistanceStore.update((s) => {
                    s.stateId = id!;
                  })
                }
                textFieldProps={{
                  variant: "outlined",
                }}
                labelField={"stateName"}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={11} sm={12} md={12}>
              <label>
                {t({ id: "skuFormCreditSubmissionExpirationDateLabel" })}
              </label>
            </Grid>
            <Grid item xs={11} sm={12} md={4}>
              <FormControl fullWidth>
                <DatePicker
                  label={t({ id: "fromDateLabel" })}
                  value={creditSubmissionExpirationDateFromStored ?? null}
                  format={standardDateFormat}
                  onChange={(date: any) => onUpdateStartDate(date)}
                  slotProps={{
                    textField: {
                      variant: "standard",
                    },
                    actionBar: {
                      actions: ["clear", "cancel"],
                    },
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={11} sm={12} md={4}>
              <FormControl fullWidth>
                <DatePicker
                  label={t({ id: "toDateLabel" })}
                  value={creditSubmissionExpirationDateToStored ?? null}
                  format={standardDateFormat}
                  onChange={(date: any) => onUpdateEndDate(date)}
                  slotProps={{
                    textField: {
                      variant: "standard",
                    },
                    actionBar: {
                      actions: ["clear", "cancel"],
                    },
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default SkuQualificationFilter;
